import { useState } from "react";

import { amountToCents, sanitizedAmountAsDecimal } from "currency";
import { FlatList, ScrollView, View } from "react-native";
import {
  Button,
  Card,
  Divider,
  List,
  Text,
  useTheme,
} from "react-native-paper";
import { useTailwind } from "tailwind-rn";
import {
  Price,
  getSavingsPercent,
  getWorstPrice,
  priceIntervalText,
} from "utils";

import { Form, Input } from "../../../components";
import Label from "../../../components/Label";
import DiscountablePriceInfo from "../components/DiscountablePriceInfo";
import DiscountablePriceText from "../components/DiscountablePriceText";
import { FormikFormSwitch } from "../components/FormSwitch";
import PriceInfoBanner from "../components/PriceInfoBanner";
import useProductFormAPI from "../contexts/ProductFormAPIContext";
import useProductFormFormik from "../hooks/useProductFormFormik";
import useProductFormNavigation from "../hooks/useProductFormNavigation";
import { productFormRoutes } from "../navigation/types";

export default function PricesScreen() {
  const { values } = useProductFormFormik();

  const showList =
    values.prices.length > 1 || values.prices.some((p) => p.name);

  return (
    <ScrollView bounces={false}>
      {showList ? <PriceList /> : <SimplePrice />}
    </ScrollView>
  );
}

function PriceList() {
  const tailwind = useTailwind();
  const { values } = useProductFormFormik();

  const showSavingsSwitch =
    values.prices.length > 1 &&
    values.prices.every((p) => p.payment_plan_type === "recurring");

  const worstPrice = getWorstPrice(values.prices);
  function amtSavedPercent(price: Price) {
    return showSavingsSwitch && values.show_savings_across_prices
      ? getSavingsPercent(worstPrice, price)
      : 0;
  }

  return (
    <Card style={tailwind("m-3 rounded-xl mb-24")}>
      <FlatList
        data={values.prices}
        scrollEnabled={false}
        ListHeaderComponent={
          <View style={tailwind("gap-8 px-4 pt-4")}>
            {showSavingsSwitch && <ShowSavingsSwitch />}
            <CreatePriceButton />
            <Text variant="titleMedium">Precios</Text>
          </View>
        }
        renderItem={({ item, index }) => (
          <PriceItem
            key={index}
            item={item}
            index={index}
            savedPercent={amtSavedPercent(item)}
          />
        )}
        ItemSeparatorComponent={() => <Divider style={tailwind("mx-4")} />}
      />
    </Card>
  );
}

function PriceItem({
  item,
  index,
  savedPercent,
}: {
  item: Price;
  index: number;
  savedPercent: number;
}) {
  const tailwind = useTailwind();
  const { colors } = useTheme();
  const { navigate } = useProductFormNavigation();

  function onItemPress() {
    navigate(productFormRoutes.priceForm, { selectedPriceIndex: index });
  }

  function parseAmount(amount: number) {
    if (item.installments_count === null) {
      return amount;
    } else {
      return amount / item.installments_count;
    }
  }

  return (
    <List.Item
      title={() => (
        <View style={tailwind("gap-1")}>
          <Text variant="bodyLarge" style={{ color: colors.secondary }}>
            {item.name}
          </Text>
          <View style={tailwind("flex-row items-center gap-1")}>
            <DiscountablePriceText
              textProps={{
                variant: "bodyLarge",
                style: [
                  tailwind("font-bold"),
                  { color: colors.onSurfaceVariant },
                ],
              }}
              price={parseAmount(item.amount)}
              priceNotDiscounted={
                item.amount_not_discounted &&
                parseAmount(item.amount_not_discounted)
              }
              currency="usd"
            />
            <Text
              variant="bodyMedium"
              style={{ color: colors.onSurfaceVariant }}
            >
              {priceIntervalText(item)}
            </Text>
          </View>
          <View style={tailwind("flex-row gap-3")}>
            <PaymentPlanTypeLabel type={item.payment_plan_type} />
            {item.sold_out && <Label name="Agotado" />}
            {savedPercent ? <Label name={`Ahorras ${savedPercent}%`} /> : null}
          </View>
        </View>
      )}
      right={(props) => (
        <List.Icon icon="menu-right" {...props} color={colors.primary} />
      )}
      onPress={onItemPress}
    />
  );
}

function PaymentPlanTypeLabel({ type }: { type: Price["payment_plan_type"] }) {
  function getText() {
    switch (type) {
      case "one_off":
        return "Único";
      case "recurring":
        return "Suscripción";
      case "installment":
        return "Plan de pagos";
    }
  }

  const text = getText();

  return <Label name={text} />;
}

function SimplePrice() {
  const tailwind = useTailwind();

  const { navigate } = useProductFormNavigation();
  const { currency } = useProductFormAPI();

  const { values, savePrice } = useProductFormFormik();

  function cents(amount: string) {
    return Number(amountToCents({ amount: Number(amount), currency }));
  }

  const savedPrice = values.prices.length > 0 ? values.prices[0] : undefined;

  const initAmount = savedPrice
    ? sanitizedAmountAsDecimal(savedPrice.amount, currency)
    : "";

  const initDiscount =
    savedPrice && savedPrice.amount_not_discounted
      ? sanitizedAmountAsDecimal(savedPrice.amount_not_discounted, currency)
      : "";

  const [amount, setAmount] = useState(initAmount);
  const [amountNotDiscounted, setAmountNotDiscounted] = useState(initDiscount);

  const error =
    amountNotDiscounted && cents(amountNotDiscounted) <= cents(amount)
      ? "El monto antes de descuento debe ser mayor al monto actual."
      : "";

  function updateSimplePrice(
    key: "amount" | "amount_not_discounted",
    value: string
  ) {
    if (savedPrice) {
      savePrice(
        {
          ...savedPrice,
          amount: cents(amount),
          amount_not_discounted: cents(amountNotDiscounted),
          [key]: cents(value),
        },
        0
      );
    } else {
      savePrice(
        {
          name: "",
          amount: cents(amount),
          amount_not_discounted: cents(amountNotDiscounted),
          payment_plan_type: "one_off",
          installments_count: null,
          recurring_interval: null,
          recurring_interval_count: 0,
          [key]: cents(value),
        },
        0
      );
    }
  }

  function onButtonPress() {
    navigate(productFormRoutes.priceForm, { selectedPriceIndex: 0 });
  }

  return (
    <View>
      <PriceInfoBanner mode="one_off" />
      <Card style={tailwind("m-3 rounded-xl")}>
        <Card.Content style={tailwind("gap-4")}>
          <Form.Field
            label="Monto"
            required
            info="* Requerido. El monto que se cobrará por el producto"
          >
            <Input.Currency
              value={amount}
              onChangeText={(text) => {
                setAmount(text);
                updateSimplePrice("amount", text);
              }}
              currency={currency}
            />
          </Form.Field>
          <Form.Field
            label="Monto antes de descuento"
            info={
              <DiscountablePriceInfo
                amount={amount}
                amountNotDiscounted={amountNotDiscounted}
                currency={currency}
              />
            }
            error={error}
          >
            <Input.Currency
              value={amountNotDiscounted}
              onChangeText={(text) => {
                setAmountNotDiscounted(text);
                updateSimplePrice("amount_not_discounted", text);
              }}
              currency={currency}
            />
          </Form.Field>
        </Card.Content>
      </Card>
      <Button
        mode="contained-tonal"
        style={tailwind("self-center")}
        onPress={onButtonPress}
      >
        Ver opciones avanzadas
      </Button>
    </View>
  );
}

function ShowSavingsSwitch() {
  const { values } = useProductFormFormik();

  const title = `${
    values.show_savings_across_prices ? "Desactivar" : "Activar"
  } cálculo de ahorro`;
  const body = `Pulsa aquí para ${
    values.show_savings_across_prices ? "desactivar" : "activar"
  } el cálculo de ahorro entre precios.`;

  return (
    <FormikFormSwitch
      fieldName="show_savings_across_prices"
      title={title}
      body={body}
    />
  );
}

function CreatePriceButton() {
  const { navigate } = useProductFormNavigation();
  const tailwind = useTailwind();

  return (
    <Button
      mode="contained-tonal"
      style={tailwind("self-center")}
      onPress={() => navigate(productFormRoutes.priceForm)}
    >
      Crear nuevo precio
    </Button>
  );
}
